'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { GoogleTagManager } from '@next/third-parties/google';

// Price pages only. Every other page (landings, the Nokhbegan checkout, registration forms) keeps
// the normal immediate GTM, because their conversion events must not wait for the page to settle.
const DEFERRED_PATHS = /^\/(symbol|markets)(\/|$)/;
const FALLBACK_DELAY_MS = 4000;
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;

/**
 * On /symbol and /markets, GTM (which carries GA4 and Google Ads) starts on the first interaction,
 * or ~4 s after the page has loaded, instead of during hydration: its scripts were the long tasks
 * in the first seconds. Events pushed to `window.dataLayer` before it starts are queued and
 * processed by GTM when it loads, so nothing is lost; consent defaults come from the
 * `consent-default` script in the layout, which runs first.
 */
export default function DeferredGoogleTagManager({ gtmId }: { gtmId: string }) {
    const pathname = usePathname() || '';
    const deferred = DEFERRED_PATHS.test(pathname);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!deferred) {
            // Loaded here once, it stays mounted when the visitor later opens a price page
            setReady(true);
            return;
        }
        let timer: ReturnType<typeof setTimeout> | undefined;
        const cleanup = () => {
            INTERACTION_EVENTS.forEach((name) => window.removeEventListener(name, start));
            window.removeEventListener('load', onLoad);
            if (timer) clearTimeout(timer);
        };
        const start = () => {
            cleanup();
            setReady(true);
        };
        const onLoad = () => {
            timer = setTimeout(start, FALLBACK_DELAY_MS);
        };

        INTERACTION_EVENTS.forEach((name) =>
            window.addEventListener(name, start, { once: true, passive: true }),
        );
        if (document.readyState === 'complete') onLoad();
        else window.addEventListener('load', onLoad, { once: true });

        return cleanup;
    }, [deferred]);

    return ready || !deferred ? <GoogleTagManager gtmId={gtmId} /> : null;
}
